import type { CogoDataSource } from "@cogo/types"

/** Minimal `fetch` shape the manager depends on (host may inject a sandbox-aware one). */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

export interface DataSourceManagerOptions {
  /**
   * Fetch implementation. Hosts inject their sandbox-aware adapter
   * (`platformAdapter.fetch`) so REST/Supabase/Gemini loads honor edit-mode
   * credential interception + relative-URL resolution. Defaults to `globalThis.fetch`.
   */
  fetch?: FetchLike
  /**
   * Extra headers merged into every REST/Supabase/Gemini request — e.g. the host's
   * merchant headers (`X-Merchant-Id`). Called per request so a late-bound id is picked up.
   */
  extraHeaders?: () => Record<string, string>
}

/**
 * Dotted-path extraction with app-runtime `applyResponseMapping` parity: returns
 * the ORIGINAL data when the path hits a non-object or yields null/undefined
 * (NOT `undefined`), so unifying the inline loaders onto this manager is
 * behavior-preserving.
 */
function extractMappedPath(data: unknown, mapping: string): unknown {
  const keys = mapping.split(".")
  let current: unknown = data
  for (const key of keys) {
    if (current == null || typeof current !== "object") return data
    current = (current as Record<string, unknown>)[key]
  }
  return current ?? data
}

/**
 * Strict dotted-path extraction — yields `undefined` when the path breaks (the
 * original `extractPath` behavior). Used for Gemini, whose pre-existing contract
 * returned `undefined` (NOT the raw response) for a missing/stale responseMapping.
 */
function extractStrictPath(data: unknown, mapping: string): unknown {
  return mapping.split(".").reduce<unknown>(
    (acc, key) => (acc as Record<string, unknown> | null | undefined)?.[key],
    data,
  )
}

export class DataSourceManager {
  private sources: Record<string, CogoDataSource>
  private cache = new Map<string, unknown>()
  private basePath: string
  private fetchFn: FetchLike
  private extraHeaders: () => Record<string, string>

  constructor(sources: Record<string, CogoDataSource>, basePath = "", options: DataSourceManagerOptions = {}) {
    this.sources = sources
    this.basePath = basePath
    this.fetchFn = options.fetch ?? ((input, init) => fetch(input, init))
    this.extraHeaders = options.extraHeaders ?? (() => ({}))
  }

  async load(name: string): Promise<unknown> {
    if (this.cache.has(name)) return this.cache.get(name)
    const source = this.sources[name]
    if (!source) throw new Error(`DataSource not found: ${name}`)
    const data = await this.resolveSource(name, source)
    this.cache.set(name, data)
    return data
  }

  /**
   * Load a SPECIFIC source definition directly — not via the registered-name map
   * and not cached. Hosts use this for block-local sources so a block datasource
   * that shares a name with an app-level one resolves the BLOCK's own definition
   * (avoids the single name→source namespace collision).
   */
  async loadSource(source: CogoDataSource, name = "datasource"): Promise<unknown> {
    return this.resolveSource(name, source)
  }

  private async resolveSource(name: string, source: CogoDataSource): Promise<unknown> {
    let data: unknown

    if (source.type === "static") {
      if (source.data != null) {
        data = structuredClone(source.data)
      } else if (source.path) {
        const rawPath = source.path
        const url = rawPath.startsWith("http")
          ? rawPath
          : rawPath.startsWith("/")
            ? `${this.basePath}${rawPath}`
            : `${this.basePath}/${rawPath}`
        const res = await this.fetchFn(url)
        if (!res.ok) throw new Error(`Failed to load: ${url}`)
        data = await res.json()
      } else {
        data = null
      }
    } else if (source.type === "rest" || (source.type as string) === "restApi" || (source.type as string) === "restful") {
      // `rest` is eagerly fetched by the host at init; `restApi`/`restful` are
      // flow-invoked (loaded on demand via the loadDatasource action) — all share
      // the endpoint/method/headers/responseMapping shape.
      // `body`/`query` are carried by a flow-invoked loadDatasource step (Lane B-4
      // restful_data_action reconciliation) — the engine pre-resolves their #{} and
      // the def url before calling loadSource (this manager has no evaluator).
      const rs = source as { endpoint?: string; url?: string; method?: string; headers?: Record<string, string>; responseMapping?: string; body?: unknown; query?: Record<string, unknown> }
      // `rest` uses `endpoint`; `restApi`/`restful` (commerce data.json + manifest)
      // use `url` — accept either.
      let target = rs.endpoint ?? rs.url
      if (!target) throw new Error(`DataSource "${name}": rest source has no endpoint/url`)
      // Merge a step `query` object into the URL (paginated/filtered GET loads).
      if (rs.query && typeof rs.query === "object") {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(rs.query)) qs.set(k, String(v))
        const q = qs.toString()
        if (q) target += (target.includes("?") ? "&" : "?") + q
      }
      const method = rs.method ?? "GET"
      const init: RequestInit = { method, headers: { ...rs.headers, ...this.extraHeaders() } }
      // Send a JSON body on a non-GET mutation (mirror actionRestApi).
      if (rs.body != null && method.toUpperCase() !== "GET") {
        init.body = typeof rs.body === "string" ? rs.body : JSON.stringify(rs.body)
        const h = init.headers as Record<string, string>
        if (!h["Content-Type"]) h["Content-Type"] = "application/json"
      }
      const res = await this.fetchFn(target, init)
      // Include the status so consumers reading `{name}_error` can branch on it
      // (parity with the host inline loaders that threw `HTTP <status>`).
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${target}`)
      const json = await res.json()
      data = rs.responseMapping
        ? extractMappedPath(json, rs.responseMapping)
        : json
    } else if (source.type === "supabase") {
      // Cross-schema table query via PostgREST. Uses Accept-Profile header
      // to route to non-public schemas (e.g. "commerce"). Supabase URL +
      // anon key come from Vite env; fall back to window.__SUPABASE__ if
      // injected by the host.
      const env = (typeof globalThis !== "undefined" && "import" in globalThis)
        ? (globalThis as any).import?.meta?.env ?? {}
        : {}
      const hostCfg = (typeof globalThis !== "undefined" && "__SUPABASE__" in globalThis)
        ? (globalThis as any).__SUPABASE__
        : null
      const supabaseUrl = source.url ?? env.VITE_SUPABASE_URL ?? hostCfg?.url ?? ""
      const anonKey = source.anonKey ?? env.VITE_SUPABASE_ANON_KEY ?? hostCfg?.anonKey ?? ""
      if (!supabaseUrl || !anonKey) {
        throw new Error(`Supabase datasource "${name}": missing url or anonKey`)
      }
      const schema = source.schema ?? "public"
      const table = source.table ?? name
      const method = String(source.method ?? "select").toLowerCase()
      if (method !== "select") {
        throw new Error(`Supabase datasource "${name}": method "${method}" not supported in datasource-manager (use ActionFlow for writes)`)
      }
      const select = source.select ?? "*"
      const limit = source.limit
      const order = source.order
      const params = new URLSearchParams()
      params.set("select", String(select))
      if (limit) params.set("limit", String(limit))
      if (order) params.set("order", String(order))
      const url = `${String(supabaseUrl).replace(/\/+$/, "")}/rest/v1/${encodeURIComponent(String(table))}?${params.toString()}`
      const headers: Record<string, string> = {
        "apikey": String(anonKey),
        "Authorization": `Bearer ${String(anonKey)}`,
        "Accept": "application/json",
        ...this.extraHeaders(),
      }
      if (schema !== "public") {
        headers["Accept-Profile"] = String(schema)
      }
      const res = await this.fetchFn(url, { method: "GET", headers })
      if (!res.ok) {
        const body = await res.text().catch(() => "")
        throw new Error(`Supabase ${schema}.${table}: ${res.status} ${body.slice(0, 200)}`)
      }
      data = await res.json()
    } else if (source.type === "supabase-auth") {
      // supabase-auth datasource returns the auth configuration for the block (D-04, D-06)
      data = {
        providers: source.providers ?? ["google"],
        redirectUrl: source.redirectUrl ?? `${typeof globalThis !== "undefined" && "location" in globalThis ? (globalThis as any).location.origin : ""}/auth/callback`,
        mfaRequired: source.mfaRequired ?? false,
      }
    } else if (source.type === "supabase-storage") {
      // supabase-storage datasource returns the storage configuration for the block (D-05, D-07)
      data = {
        bucket: source.bucket ?? "public",
        allowedFileTypes: source.allowedFileTypes ?? ["image/*"],
        maxFileSize: source.maxFileSize ?? 5 * 1024 * 1024, // 5MB default
        isPublic: source.isPublic ?? true,
      }
    } else if (source.type === "gemini") {
      const model = source.model ?? "gemini-2.5-pro"
      const res = await this.fetchFn(
        `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`,
        {
          method: "POST",
          headers: {
            "x-goog-api-key": source.apiKey ?? "",
            "Content-Type": "application/json",
            ...this.extraHeaders(),
          },
          body: JSON.stringify({
            contents: [{ role: "user", parts: [{ text: source.prompt ?? "" }] }],
          }),
        },
      )
      if (!res.ok) throw new Error(`Gemini API error: ${res.status}`)
      const json = await res.json() as Record<string, unknown>
      if (source.responseMapping) {
        // Gemini keeps its strict (undefined-on-break) mapping — unlike REST,
        // which adopts applyResponseMapping (original-on-break) parity.
        data = extractStrictPath(json, source.responseMapping)
      } else {
        // Default: extract text from first candidate
        const candidates = json.candidates as Array<Record<string, unknown>> | undefined
        const firstCandidate = candidates?.[0]
        const content = firstCandidate?.content as Record<string, unknown> | undefined
        const parts = content?.parts as Array<Record<string, unknown>> | undefined
        data = parts?.[0]?.text ?? json
      }
    }

    return data
  }

  preload(name: string, data: unknown): void {
    this.cache.set(name, data)
  }

  invalidate(name: string): void {
    this.cache.delete(name)
  }

  invalidateAll(): void {
    this.cache.clear()
  }

  /**
   * Register a datasource definition for `load(name)` resolution. First
   * registration wins (does NOT override) so resolution is order-stable: the
   * app-level/manifest def — seeded at construction — is kept on a name clash.
   *
   * NOTE (known limitation): datasource ids are a single global namespace here,
   * but the loadDatasource ACTION only carries a bare id. So if two blocks (or a
   * block and the manifest) reuse one name, the action resolves the first/manifest
   * def — it is NOT block-scoped. The eager init loaders are unaffected (they
   * fetch each def directly via loadSource). Block-scoped action resolution would
   * require threading block context into the action — deferred as future work.
   */
  registerSource(name: string, source: CogoDataSource): void {
    if (!(name in this.sources)) {
      this.sources[name] = source
    }
  }

  /**
   * Return a registered source definition by name — used by the `loadDatasource`
   * action's rest-subset override (Lane B-4) to build a merged def (engine resolves
   * the def url #{} + merges step method/body/query) for an UNCACHED loadSource call.
   * Returns undefined when the name is not registered.
   */
  getSource(name: string): CogoDataSource | undefined {
    return this.sources[name]
  }
}
